import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Calendar, ArrowRight, Trophy, Clock, MapPin, Bell, Tv } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import EventAlertModal from "@/components/EventAlertModal";
import { format } from "date-fns";

interface BroadcastInfo {
  country: string;
  flag: string;
  channels: string[];
}

interface BigEvent {
  id: string;
  title: string;
  subtitle: string;
  icon: string;
  startDate: Date;
  endDate: Date;
  location: string;
  nextGame?: {
    teams: string;
    flags: string;
    date: Date;
    time: string;
    stage: string;
  };
  link?: string;
  linkText?: string;
  isPlaceholder?: boolean;
  broadcasts?: BroadcastInfo[];
}

// Broadcast mappings by event type for all supported countries
const getOlympicsBroadcasts = (): BroadcastInfo[] => [
  { country: "USA", flag: "🇺🇸", channels: ["NBC", "Peacock", "USA Network"] },
  { country: "Canada", flag: "🇨🇦", channels: ["CBC", "TSN", "Sportsnet"] },
  { country: "Sweden", flag: "🇸🇪", channels: ["SVT / TV4"] },
  { country: "Finland", flag: "🇫🇮", channels: ["Yle", "MTV3", "Discovery+"] },
  { country: "Russia", flag: "🇷🇺", channels: ["Матч ТВ", "Первый канал"] },
  { country: "Germany", flag: "🇩🇪", channels: ["ARD", "ZDF", "Eurosport"] },
  { country: "Czechia", flag: "🇨🇿", channels: ["ČT Sport", "Nova Sport"] },
];

const getNHLBroadcasts = (): BroadcastInfo[] => [
  { country: "USA", flag: "🇺🇸", channels: ["ESPN", "TNT", "ESPN+"] },
  { country: "Canada", flag: "🇨🇦", channels: ["Sportsnet", "TSN", "TVA Sports"] },
  { country: "Sweden", flag: "🇸🇪", channels: ["Viaplay / TV6"] },
  { country: "Finland", flag: "🇫🇮", channels: ["Viaplay", "V Sport"] },
  { country: "Russia", flag: "🇷🇺", channels: ["Матч ТВ", "Okko Sport"] },
  { country: "Germany", flag: "🇩🇪", channels: ["Sky Sport", "Sportdigital"] },
  { country: "Czechia", flag: "🇨🇿", channels: ["Nova Sport", "Voyo"] },
];

const getKHLBroadcasts = (): BroadcastInfo[] => [
  { country: "Russia", flag: "🇷🇺", channels: ["КХЛ ТВ", "Матч ТВ"] },
  { country: "Finland", flag: "🇫🇮", channels: ["Viaplay"] },
  { country: "Czechia", flag: "🇨🇿", channels: ["Nova Sport"] },
  { country: "Germany", flag: "🇩🇪", channels: ["Sportdigital"] },
];

const getAHLBroadcasts = (): BroadcastInfo[] => [
  { country: "USA", flag: "🇺🇸", channels: ["AHL TV", "FloHockey"] },
  { country: "Canada", flag: "🇨🇦", channels: ["AHL TV", "TSN"] },
];

const getECHLBroadcasts = (): BroadcastInfo[] => [
  { country: "USA", flag: "🇺🇸", channels: ["FloHockey", "ECHL TV"] },
];

const getSHLBroadcasts = (): BroadcastInfo[] => [
  { country: "Sweden", flag: "🇸🇪", channels: ["C More / TV4"] },
  { country: "Finland", flag: "🇫🇮", channels: ["C More"] },
];

const getIIHFBroadcasts = (): BroadcastInfo[] => [
  { country: "USA", flag: "🇺🇸", channels: ["NHL Network", "ESPN+"] },
  { country: "Canada", flag: "🇨🇦", channels: ["TSN", "RDS"] },
  { country: "Sweden", flag: "🇸🇪", channels: ["SVT / TV4"] },
  { country: "Finland", flag: "🇫🇮", channels: ["Yle", "MTV3"] },
  { country: "Russia", flag: "🇷🇺", channels: ["Матч ТВ"] },
  { country: "Germany", flag: "🇩🇪", channels: ["Sport1", "MagentaSport"] },
  { country: "Czechia", flag: "🇨🇿", channels: ["ČT Sport", "Nova Sport"] },
];

const UpcomingBigEvents = () => {
  const { t } = useLanguage();
  const events: BigEvent[] = useMemo(() => {

    const u18WorldJuniors: BigEvent = {
      id: "u18-world-juniors-2026",
      title: "U18 World Championship",
      subtitle: "World Juniors (U18)",
      icon: "🏒",
      startDate: new Date("2026-04-16"),
      endDate: new Date("2026-04-26"),
      location: "United States",
      isPlaceholder: true,
      broadcasts: getIIHFBroadcasts(),
    };

    const womensWorlds: BigEvent = {
      id: "womens-worlds-2026",
      title: "Women's World Championship",
      subtitle: "IIHF Women's Worlds",
      icon: "👩‍🦰",
      startDate: new Date("2026-04-09"),
      endDate: new Date("2026-04-20"),
      location: "Czech Republic",
      isPlaceholder: true,
      broadcasts: getIIHFBroadcasts(),
    };

    const iihfEvent: BigEvent = {
      id: "iihf-worlds-2026",
      title: "IIHF World Championship",
      subtitle: "Men's Ice Hockey Worlds",
      icon: "🌍",
      startDate: new Date("2026-05-15"),
      endDate: new Date("2026-05-31"),
      location: "Zürich & Fribourg, Switzerland",
      isPlaceholder: true,
      broadcasts: getIIHFBroadcasts(),
    };

    const shlPlayoffs: BigEvent = {
      id: "shl-playoffs-2026",
      title: "SHL Playoffs 2026",
      subtitle: "Swedish Hockey League Finals",
      icon: "🇸🇪",
      startDate: new Date("2026-03-10"),
      endDate: new Date("2026-05-01"),
      location: "Sweden",
      isPlaceholder: true,
      broadcasts: getSHLBroadcasts(),
    };

    const khlPlayoffs: BigEvent = {
      id: "khl-playoffs-2026",
      title: "KHL Gagarin Cup Playoffs",
      subtitle: "Kontinental Hockey League",
      icon: "🏆",
      startDate: new Date("2026-03-01"),
      endDate: new Date("2026-04-30"),
      location: "Russia & Europe",
      isPlaceholder: true,
      broadcasts: getKHLBroadcasts(),
    };

    const nhlPlayoffs: BigEvent = {
      id: "nhl-playoffs-2026",
      title: "NHL Stanley Cup Playoffs",
      subtitle: "National Hockey League",
      icon: "🇺🇸",
      startDate: new Date("2026-04-18"),
      endDate: new Date("2026-06-20"),
      location: "North America",
      isPlaceholder: true,
      broadcasts: getNHLBroadcasts(),
    };

    const ahlPlayoffs: BigEvent = {
      id: "ahl-playoffs-2026",
      title: "AHL Calder Cup Playoffs",
      subtitle: "American Hockey League",
      icon: "🏒",
      startDate: new Date("2026-04-22"),
      endDate: new Date("2026-06-15"),
      location: "North America",
      isPlaceholder: true,
      broadcasts: getAHLBroadcasts(),
    };

    const echlPlayoffs: BigEvent = {
      id: "echl-playoffs-2026",
      title: "ECHL Kelly Cup Playoffs",
      subtitle: "East Coast Hockey League",
      icon: "🥅",
      startDate: new Date("2026-04-10"),
      endDate: new Date("2026-06-10"),
      location: "North America",
      isPlaceholder: true,
      broadcasts: getECHLBroadcasts(),
    };

    const chlFinal: BigEvent = {
      id: "chl-final-2026",
      title: "CHL Final 2025/26",
      subtitle: "Champions Hockey League",
      icon: "🏒",
      startDate: new Date("2026-03-03"),
      endDate: new Date("2026-03-03"),
      location: "Scandinavium, Gothenburg",
      nextGame: {
        teams: "Frölunda HC 3-2 Luleå Hockey (OT)",
        flags: "🇸🇪🇸🇪",
        date: new Date("2026-03-03"),
        time: "19:00",
        stage: "Final — Completed",
      },
      broadcasts: getSHLBroadcasts(),
    };

    return [chlFinal, nhlPlayoffs, khlPlayoffs, ahlPlayoffs, echlPlayoffs, shlPlayoffs, u18WorldJuniors, womensWorlds, iihfEvent];
  }, []);

  return (
    <section className="py-10 md:py-14 relative overflow-hidden" id="big-events">
      {/* Background accent */}
      <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent pointer-events-none" />
      
      <div className="container mx-auto px-4 md:px-6 relative">
        {/* Section Header */}
        <div className="text-center mb-8 md:mb-10">
          <div className="inline-flex items-center gap-2 mb-3">
            <Trophy className="h-5 w-5 text-primary" />
            <span className="text-sm font-medium text-primary uppercase tracking-wider">
              {t.bigEvents.markCalendar}
            </span>
          </div>
          <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-bold mb-2">
            {t.bigEvents.title}
          </h2>
          <p className="text-muted-foreground text-sm md:text-base max-w-xl mx-auto mb-4">
            {t.bigEvents.subtitle}
          </p>
          <EventAlertModal
            trigger={
              <Button className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2">
                <Bell className="h-4 w-4" />
                {t.bigEvents.getAlerts}
              </Button>
            }
          />
        </div>

        {/* Events Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {events.map((event) => (
            <EventCard key={event.id} event={event} translations={t.bigEvents} />
          ))}
        </div>
      </div>
    </section>
  );
};

interface EventCardProps {
  event: BigEvent;
  translations: { whereToWatch: string; scheduleSoon: string; moreInfoSoon: string; nextGame: string };
}

const EventCard = ({ event, translations }: EventCardProps) => {
  return (
    <Card 
      className="group relative overflow-hidden border-border/50 bg-card/80 backdrop-blur-sm transition-all duration-300 hover:border-primary/50 hover:shadow-lg hover:shadow-primary/10"
    >
      {/* Hover glow effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      
      <CardContent className="p-5 md:p-6 relative">
        {/* Event Header */}
        <div className="flex items-start gap-3 mb-4">
          <div className="text-3xl md:text-4xl">{event.icon}</div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-lg md:text-xl font-semibold text-foreground group-hover:text-primary transition-colors truncate">
              {event.title}
            </h3>
            <p className="text-sm text-muted-foreground">{event.subtitle}</p>
          </div>
        </div>

        {/* Event Details */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Calendar className="h-4 w-4 text-primary/70" />
            <span>
              {format(event.startDate, 'MMM d')} – {format(event.endDate, 'MMM d, yyyy')}
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <MapPin className="h-4 w-4 text-primary/70" />
            <span>{event.location}</span>
          </div>
        </div>

        {/* Next Game Preview (Olympics only) */}
        {event.nextGame && (
          <div className="bg-muted/50 rounded-lg p-3 mb-4 border border-border/50">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="h-3.5 w-3.5 text-primary" />
              <span className="text-xs font-medium text-primary uppercase tracking-wide">
                {translations.nextGame} – {event.nextGame.stage}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-lg">{event.nextGame.flags}</span>
              <span className="text-sm font-medium text-foreground flex-1 truncate">
                {event.nextGame.teams}
              </span>
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {format(event.nextGame.date, 'EEEE, MMM d')} • {event.nextGame.time} Europe/Rome
            </div>
          </div>
        )}

        {/* Where to Watch */}
        {event.broadcasts && event.broadcasts.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <Tv className="h-4 w-4 text-primary/70" />
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                {translations.whereToWatch}
              </span>
            </div>
            <div className="grid grid-cols-1 gap-1.5 max-h-32 overflow-y-auto">
              {event.broadcasts.map((broadcast) => (
                <div 
                  key={broadcast.country} 
                  className="flex items-center gap-2 text-xs bg-muted/30 rounded px-2 py-1"
                >
                  <span className="text-sm">{broadcast.flag}</span>
                  <span className="text-muted-foreground font-medium min-w-[60px]">{broadcast.country}:</span>
                  <span className="text-foreground truncate">{broadcast.channels.join(", ")}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Placeholder badge */}
        {event.isPlaceholder && (
          <Badge variant="secondary" className="mb-4">
            {translations.scheduleSoon}
          </Badge>
        )}

        {/* CTA Button */}
        {event.link ? (
          <Link to={event.link} className="block">
            <Button 
              variant="outline" 
              className="w-full group/btn border-primary/30 hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-200"
            >
              <span>{event.linkText}</span>
              <ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover/btn:translate-x-1" />
            </Button>
          </Link>
        ) : (
          <Button 
            variant="ghost" 
            className="w-full text-muted-foreground cursor-default"
            disabled
          >
            {translations.moreInfoSoon}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default UpcomingBigEvents;
